/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import {
    DisplayTimezoneSelect,
    TimezoneLabel,
    useActiveTimezone,
} from 'components/app/common/displayTimezoneSelect';

const STORAGE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const INPUT_FORMAT = 'YYYY-MM-DDTHH:mm';

/**
 * Datetime widget for react-awesome-query-builder that:
 * - Stores the value as a UTC string (YYYY-MM-DD HH:mm:ss).
 * - Renders the input in the user's active display timezone.
 * - Shows a read-only TimezoneLabel so the user knows which TZ is used.
 *
 * RAQB invokes this factory once per cardinality slot (e.g. 2 for `between`),
 * each slot owning its own value/setValue, so no special handling is needed.
 */
const DatetimeTzWidget = (props) => {
    const { value, setValue, readonly } = props;

    const { activeTimezone } = useActiveTimezone();
    const tz = activeTimezone || moment.tz.guess();
    const [showTimezoneModal, setTimezoneModal] = useState(false);

    const displayValue = useMemo(() => {
        if (!value) return '';
        const m = moment.utc(value, STORAGE_FORMAT, true);
        if (!m.isValid()) {
            const fallback = moment.utc(value);
            return fallback.isValid()
                ? fallback.tz(tz).format(INPUT_FORMAT)
                : '';
        }
        return m.tz(tz).format(INPUT_FORMAT);
    }, [value, tz]);

    const onChange = (e) => {
        const v = e.target.value;
        if (!v) {
            setValue(undefined);
            return;
        }
        const utcStr = moment
            .tz(v, INPUT_FORMAT, tz)
            .utc()
            .format(STORAGE_FORMAT);
        setValue(utcStr);
    };

    return (
        <div className="qb-datetime-tz">
            <input
                type="datetime-local"
                value={displayValue}
                disabled={readonly}
                onChange={onChange}
            />
            <TimezoneLabel
                tZstyle={{ textAlign: 'left', margin: '5px 0 0px 0px' }}
                setIsModalVisible={setTimezoneModal}
            />
            <DisplayTimezoneSelect
                isModalVisible={showTimezoneModal}
                setIsModalVisible={setTimezoneModal}
            />
        </div>
    );
};

export default DatetimeTzWidget;
